// src/components/doctor/RiskTrendCard.jsx
//
// The Risk tile on the doctor's patient page. Answers, at a glance: how risky
// is this patient now, is that better or worse than last time (and by enough
// to matter), what has moved, and how does it sit against the risk bands.
//
// Colour follows *harm*, not arithmetic: a higher score is worse, so a rise is
// amber/red and a fall is green — a green ▲ on a rising risk score read as
// "improving". A rise inside the same band stays quiet (amber chip); crossing
// into a higher band is the loud state (red chip). The tile keeps the same
// border as the other snapshot tiles, so their content lines up.
//
// Styling lives in index.css under .patient-snapshot. At >=1200px the tile is
// laid out compactly (header with the link, value block beside the chart,
// drivers below) and the chart is drawn at its real pixel size; below that it
// renders as a plain stacked tile.

import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { parseReportMarkers, NORMAL_STATUSES } from '../PatientResultSections';

// Same cut-offs the report screens use (>=70 high, >=40 moderate).
const BAND_MODERATE = 40;
const BAND_HIGH = 70;
const RANK = { LOW: 0, MODERATE: 1, HIGH: 2, CRITICAL: 3 };
const BADGE = {
  LOW: 'badge-low', MODERATE: 'badge-moderate', HIGH: 'badge-critical', CRITICAL: 'badge-critical',
};
const LINE = {
  LOW: 'var(--green)', MODERATE: 'var(--amber)', HIGH: 'var(--red)', CRITICAL: 'var(--red)',
};
const MAX_POINTS = 8;
const MAX_NAMES = 2;

// Marker severity, so "got worse" can be told apart from "still flagged".
const STATUS_RANK = { normal: 0, optimal: 0, low: 1, elevated: 1, high: 2, critical: 3 };

const reportDate = r => r.drawDate || r.createdAt;
const fmtShort = v => new Date(v).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
const fmtFull = v => new Date(v).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const levelOf = r => (r?.consensusRiskLevel || '').toUpperCase();

function markerStatus(m) {
  return (m.status || m.severity || 'normal').toLowerCase();
}
const markerName = m => (m.name || m.marker || '').trim();
const arrow = status => (status === 'low' ? '↓' : '↑');

// "Worse since last report" (newly flagged or more severe), else "Still flagged".
export function riskDrivers(latest, previous) {
  const now = parseReportMarkers(latest).filter(m => markerName(m));
  const flagged = now.filter(m => !NORMAL_STATUSES.has(markerStatus(m)));
  if (previous) {
    const before = new Map(parseReportMarkers(previous).map(m => [markerName(m), markerStatus(m)]));
    // A marker the previous report didn't test is unknown, not "newly flagged".
    const worse = flagged.filter(m => before.has(markerName(m))
      && (STATUS_RANK[markerStatus(m)] ?? 1) > (STATUS_RANK[before.get(markerName(m))] ?? 0));
    if (worse.length) return { kind: 'worse', list: worse };
  }
  return { kind: flagged.length ? 'still' : 'none', list: flagged };
}

const driverName = (m, kind) => `${markerName(m)}${kind === 'worse' ? ` ${arrow(markerStatus(m))}` : ''}`;

function DriverText({ drivers }) {
  if (drivers.kind === 'none') return <span className="risk-card-drivers">No flagged markers</span>;
  const prefix = drivers.kind === 'worse' ? 'Worse since last report: ' : 'Still flagged: ';
  const shown = drivers.list.slice(0, MAX_NAMES);
  const more = drivers.list.length - shown.length;
  // The full list, for when the line is clamped (or names are cut to +N).
  const full = prefix + drivers.list.map(m => driverName(m, drivers.kind)).join(', ');
  return (
    <span className="risk-card-drivers" title={full}>
      {prefix}
      {shown.map((m, i) => (
        <b key={markerName(m)} className="risk-card-marker">
          {i > 0 && ', '}{markerName(m)}{drivers.kind === 'worse' ? ` ${arrow(markerStatus(m))}` : ''}
        </b>
      ))}
      {more > 0 && ` +${more}`}
    </span>
  );
}

// Fixed 0–100 scale: a 27→38 wobble inside LOW must look like a wobble.
const BANDS = [
  [BAND_HIGH, 'HIGH', 'var(--red)', 'var(--red)', 'High'],
  [BAND_MODERATE, 'MODERATE', 'var(--amber)', 'var(--amber-text)', 'Moderate'],
];

// Stacked tile: a 300x92 viewBox scaled to the tile's width.
const STACKED = {
  w: 300, h: 92, left: 0, padX: 14, top: 8, bottom: 72, font: 9, tipFont: 10.5, dateY: 90,
};
// Compact tile (>=1200px): drawn at its rendered pixel width and a fixed
// height, so its text stays 11px; band values sit in a narrow left margin.
const COMPACT_QUERY = '(min-width: 1200px)';
const COMPACT_H = 76;
const COMPACT_GUTTER = 24;
const compactGeometry = w => ({
  w, h: COMPACT_H, left: COMPACT_GUTTER, padX: 10, top: 6, bottom: 58, font: 11, tipFont: 11, dateY: COMPACT_H - 2,
});

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = e => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

function useWidth(ref, enabled) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, enabled]);
  return width;
}

function TrendChart({ points, color }) {
  const compact = useMediaQuery(COMPACT_QUERY);
  const ref = useRef(null);
  const measured = useWidth(ref, compact);
  const g = compact ? compactGeometry(measured) : STACKED;
  const [active, setActive] = useState(null);
  const n = points.length;
  const label = `Risk score over ${n} report${n === 1 ? '' : 's'}: `
    + points.map(p => `${fmtFull(p.date)} ${p.score}`).join(', ');

  // Until the compact chart knows its width, draw an empty box of its height.
  if (compact && !g.w) {
    return <svg ref={ref} className="risk-chart" width="100%" height={g.h} role="img" aria-label={label} />;
  }

  const { w: W, left: L, padX: PAD_X } = g;
  const yOf = score => g.bottom - (Math.max(0, Math.min(100, score)) / 100) * (g.bottom - g.top);
  const xOf = i => (n === 1 ? L + (W - L) / 2 : L + PAD_X + (i * (W - L - PAD_X * 2)) / (n - 1));
  const xy = points.map((p, i) => [xOf(i), yOf(p.score)]);
  const a = active != null ? points[active] : null;
  const ax = active != null ? xy[active][0] : 0;
  const ay = active != null ? xy[active][1] : 0;
  const tipW = 92;
  // Right of the band-label column (L is 0 in the stacked chart), inside the chart.
  const tipX = Math.min(Math.max(ax - tipW / 2, L + 2), W - tipW - 2);
  const tipY = ay > 40 ? ay - 30 : ay + 10;

  return (
    <svg ref={ref} className="risk-chart" viewBox={`0 0 ${W} ${g.h}`} width="100%"
      height={compact ? g.h : undefined} role="img" aria-label={label}>
      {BANDS.map(([v, name, stroke]) => (
        <line key={name} x1={L} x2={W} y1={yOf(v)} y2={yOf(v)} stroke={stroke} strokeOpacity=".45" strokeDasharray="3 4" />
      ))}
      {n > 1 && (
        <polyline points={xy.map(p => p.join(',')).join(' ')} fill="none" stroke={color}
          strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
      )}
      {xy.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i === n - 1 ? 4.5 : (i === active ? 4 : 2.5)}
          fill={color} />
      ))}
      {/* Drawn after the line, with a halo in the card colour, so the label stays
          legible where the line crosses it. */}
      {BANDS.map(([v, name, , fill, title]) => (compact ? (
        // Compact: just the value, in the left margin, beside its line.
        <text key={name} x={L - 5} y={yOf(v) + 4} fontSize={g.font} fill={fill} textAnchor="end"
          aria-label={`${title} ${v}`}>
          <title>{`${title} ${v}`}</title>
          {v}
        </text>
      ) : (
        <text key={name} x="2" y={yOf(v) - 4} fontSize="9" fill={fill} stroke="var(--bg-surface)"
          strokeWidth="3" paintOrder="stroke">{name} {v}</text>
      )))}
      {/* Generous invisible hit targets: hover or keyboard focus shows the value. */}
      {xy.map(([x, y], i) => (
        <circle key={`h${i}`} className="risk-chart-hit" cx={x} cy={y} r="11" fill="transparent" tabIndex={0}
          aria-label={`${fmtFull(points[i].date)}: ${points[i].score}`}
          onMouseEnter={() => setActive(i)} onMouseLeave={() => setActive(null)}
          onFocus={() => setActive(i)} onBlur={() => setActive(null)}
          onClick={e => e.stopPropagation()} />
      ))}
      {a && (
        <g pointerEvents="none">
          <rect x={tipX} y={tipY} width={tipW} height="20" rx="5"
            fill="var(--bg-hover)" stroke="var(--border-strong)" />
          <text x={tipX + tipW / 2} y={tipY + 13.5} textAnchor="middle" fontSize={g.tipFont}
            fill="var(--text-primary)">{fmtShort(a.date)} · {a.score}</text>
        </g>
      )}
      <text x={n === 1 ? L + (W - L) / 2 : L + PAD_X} y={g.dateY} fontSize={g.font} fill="var(--text-muted)"
        textAnchor={n === 1 ? 'middle' : 'start'}>{fmtShort(points[0].date)}</text>
      {n > 1 && (
        <text x={W - PAD_X} y={g.dateY} fontSize={g.font} fill="var(--text-muted)" textAnchor="end">
          {fmtShort(points[n - 1].date)}
        </text>
      )}
    </svg>
  );
}

export default function RiskTrendCard({ reports = [], onOpen }) {
  const latest = reports[0];
  const previous = reports[1];

  const points = useMemo(() => reports
    .filter(r => r.consensusRiskScore != null && reportDate(r))
    .slice(0, MAX_POINTS)
    .map(r => ({ score: r.consensusRiskScore, date: reportDate(r) }))
    .reverse(), [reports]);

  const drivers = useMemo(() => (latest ? riskDrivers(latest, previous) : null), [latest, previous]);

  if (!latest) {
    return (
      <div className="stat-card">
        <div className="stat-label">Risk</div>
        <div className="stat-value">—</div>
        <div className="stat-change">No reports yet</div>
      </div>
    );
  }

  const level = levelOf(latest);
  const score = latest.consensusRiskScore;
  const prevScore = previous?.consensusRiskScore;
  const delta = score != null && prevScore != null ? score - prevScore : null;
  const prevLevel = levelOf(previous);
  const crossedUp = level in RANK && prevLevel in RANK && RANK[level] > RANK[prevLevel];
  const crossedDown = level in RANK && prevLevel in RANK && RANK[level] < RANK[prevLevel];
  const vs = previous ? `vs ${fmtShort(reportDate(previous))}` : '';

  let chip = null;
  if (!previous) chip = { cls: 'chip-muted', text: 'First report' };
  else if (delta == null) chip = null;
  else if (crossedUp) chip = { cls: 'bad', text: `▲ ${delta} ${vs} · was ${prevLevel}` };
  else if (crossedDown) chip = { cls: 'good', text: `▼ ${Math.abs(delta)} ${vs} · was ${prevLevel}` };
  else if (delta > 0) chip = { cls: 'warn', text: `▲ ${delta} ${vs} · still ${level}` };
  else if (delta < 0) chip = { cls: 'good', text: `▼ ${Math.abs(delta)} ${vs} · still ${level}` };
  else chip = { cls: 'chip-muted', text: `No change ${vs}` };

  // The footer keeps "View reports" last in the DOM (after the chart's points),
  // so Tab goes from it to Open items; at >=1200px CSS places it in the header.
  return (
    <div className={`stat-card risk-card${onOpen ? ' risk-card--link' : ''}`} onClick={onOpen}>
      <div className="risk-card-head">
        <div className="stat-label">Risk</div>
        <div className="risk-card-latest">
          Latest report · {fmtFull(reportDate(latest))}
        </div>
      </div>

      <div className="risk-card-value">
        <span className="stat-value">{score ?? '—'}</span>
        {score != null && <span className="risk-card-max">/100</span>}
        {level && <span className={`badge ${BADGE[level] || 'badge-unknown'}`}>{level}</span>}
        {chip && (
          <span className={`chip ${chip.cls}`}>{chip.text}</span>
        )}
      </div>

      {points.length > 0 && <TrendChart points={points} color={LINE[level] || 'var(--brand-accent)'} />}

      <div className="risk-card-foot">
        <DriverText drivers={drivers} />
        {onOpen && (
          <button type="button" className="card-link risk-card-link" onClick={e => { e.stopPropagation(); onOpen(); }}>
            View reports →
          </button>
        )}
      </div>
    </div>
  );
}
